class ReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this._slides = Array.from(this.querySelectorAll('.reviews-carousel__slide'));
    this._dotsContainer = this.querySelector('[data-dots]');
    this._speed = parseInt(this.dataset.speed) || 3000;
    this._current = 0;
    this._timer = null;
    this._rtl = getComputedStyle(this).direction === 'rtl' || this.classList.contains('reviews-carousel--rtl');

    if (this._slides.length === 0) return;

    this._buildDots();
    this._show(0, 1, false);

    this.querySelector('[data-prev]')?.addEventListener('click', () => {
      this._prev();
      this._restart();
    });

    this.querySelector('[data-next]')?.addEventListener('click', () => {
      this._next();
      this._restart();
    });

    if (this._slides.length > 1) {
      this._start();
      this.addEventListener('mouseenter', () => this._pause());
      this.addEventListener('mouseleave', () => this._start());
      this._bindSwipe();
    }
  }

  disconnectedCallback() {
    this._pause();
  }

  _buildDots() {
    if (!this._dotsContainer || this._slides.length <= 1) return;

    this._slides.forEach((_, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'reviews-carousel__dot';
      btn.setAttribute('aria-label', `Review ${i + 1}`);
      btn.addEventListener('click', () => {
        if (i === this._current) return;
        this._show(i, i > this._current ? 1 : -1);
        this._restart();
      });
      this._dotsContainer.appendChild(btn);
    });

    this._dots = Array.from(this._dotsContainer.querySelectorAll('.reviews-carousel__dot'));
  }

  /**
   * Slides to `index`. `dir` is 1 for next and -1 for previous; "next" follows the
   * reading direction, so on an RTL page the new review comes in from the left.
   */
  _show(index, dir = 1, animate = true) {
    const outgoing = this._slides[this._current];
    const incoming = this._slides[index];

    if (animate && outgoing && outgoing !== incoming) {
      // Side the new review enters from, in %: + is the right, - is the left.
      const from = dir * (this._rtl ? -1 : 1) * 100;

      clearTimeout(incoming._leaveTimer);
      incoming.classList.remove('is-leaving');
      incoming.style.transition = 'none';
      incoming.style.transform = `translateX(${from}%)`;
      void incoming.offsetWidth;
      incoming.style.transition = '';
      incoming.style.transform = '';

      clearTimeout(outgoing._leaveTimer);
      outgoing.classList.add('is-leaving');
      outgoing.style.transform = `translateX(${-from}%)`;
      outgoing._leaveTimer = setTimeout(() => {
        outgoing.classList.remove('is-leaving');
        outgoing.style.transform = '';
      }, 600);
    }

    this._slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
    });

    if (this._dots) {
      this._dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
    }

    this._current = index;
  }

  _prev() {
    this._show((this._current - 1 + this._slides.length) % this._slides.length, -1);
  }

  _next() {
    this._show((this._current + 1) % this._slides.length, 1);
  }

  _bindSwipe() {
    let startX = null;
    let startY = null;

    this.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        this._pause();
      },
      { passive: true }
    );

    this.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        startX = null;

        // Horizontal swipes only, so scrolling the page still works.
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          // A swipe to the left brings the new review in from the right.
          const fromRight = dx < 0;
          if (fromRight !== this._rtl) {
            this._next();
          } else {
            this._prev();
          }
        }
        this._start();
      },
      { passive: true }
    );
  }

  _start() {
    this._pause();
    if (this._slides.length > 1) {
      this._timer = setInterval(() => this._next(), this._speed);
    }
  }

  _pause() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  _restart() {
    this._pause();
    this._start();
  }
}

customElements.define('reviews-carousel', ReviewsCarousel);
